import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Globe, Filter, X, Search, ArrowLeft, Activity, Bell } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import InteractiveWorldMap, { COUNTRY_DATA } from '@/components/insights/InteractiveWorldMap';

const API = process.env.REACT_APP_BACKEND_URL;
const REGIONS = ['Europe', 'Middle East', 'Asia-Pacific', 'Americas'];
const STATUS_COLORS = { live: '#16a34a', upcoming: '#f59e0b', monitoring: '#6366f1' };
const STATUS_LABELS = { live: 'Mandate Live', upcoming: 'Upcoming', monitoring: 'Monitoring' };
const CATEGORY_NAMES = {
  einvoicing: 'e-Invoicing & Digital VAT Tracker',
  saft: 'SAF-T & Digital Audit Tracker',
  vat_returns: 'VAT Returns & Reporting Tracker',
  vat_structure: 'VAT Structure Tracker',
};

export default function TrackerCategoryPage() {
  const { categoryId = 'einvoicing' } = useParams();
  const [countries, setCountries] = useState([]);
  const [regionFilter, setRegionFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/tracker/content?category=${categoryId}`)
      .then(r => r.json())
      .then(d => { setCountries(d.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [categoryId]);

  const filtered = useMemo(() => {
    return countries.filter(c => {
      if (regionFilter && c.region !== regionFilter) return false;
      if (statusFilter && c.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!c.country_name.toLowerCase().includes(q) && !c.layer1?.mandate_name?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [countries, regionFilter, statusFilter, search]);

  const statusCounts = useMemo(() => {
    const counts = {};
    countries.forEach(c => { counts[c.status] = (counts[c.status] || 0) + 1; });
    return counts;
  }, [countries]);

  const hasFilters = regionFilter || statusFilter || search;

  // Build map overlay data from tracker countries
  const mapOverlayData = useMemo(() => {
    const overlay = {};
    countries.forEach(c => {
      const numericId = Object.keys(COUNTRY_DATA).find(k => COUNTRY_DATA[k].iso === c.country_code);
      if (numericId) {
        overlay[numericId] = { ...COUNTRY_DATA[numericId], detail: c.layer1?.summary || COUNTRY_DATA[numericId]?.detail };
      }
    });
    return overlay;
  }, [countries]);

  return (
    <PageLayout>
      <div className="min-h-screen bg-white" data-testid="tracker-category-page">

        {/* Header */}
        <section className="pt-8 pb-4 border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-6">
            <Link to="/tracker" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-taxera-blue transition-colors mb-4" data-testid="tracker-back-link">
              <ArrowLeft className="w-3.5 h-3.5" /> Global Tax Tracker
            </Link>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal mb-1">
                  {CATEGORY_NAMES[categoryId] || 'Tax Tracker'}
                </h1>
                <p className="text-sm text-slate-500">{countries.length} countries tracked with full regulatory analysis</p>
              </div>
              <div className="hidden md:flex items-center gap-3">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(prev => prev === status ? null : status)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                      statusFilter === status ? 'text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                    style={statusFilter === status ? { backgroundColor: STATUS_COLORS[status] } : {}}
                    data-testid={`tracker-status-${status}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[status] }} />
                    {STATUS_LABELS[status] || status} ({count})
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* World Map */}
        <section className="py-8 bg-slate-50/50">
          <div className="max-w-7xl mx-auto px-6">
            <InteractiveWorldMap />
          </div>
        </section>

        {/* Filters + Country list */}
        <section className="py-8" data-testid="tracker-country-list">
          <div className="max-w-7xl mx-auto px-6">

            {/* Filter bar */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {/* Region pills */}
              <div className="flex flex-wrap items-center gap-2">
                {REGIONS.map(r => {
                  const count = countries.filter(c => c.region === r).length;
                  if (count === 0) return null;
                  return (
                    <button
                      key={r}
                      onClick={() => setRegionFilter(prev => prev === r ? null : r)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                        regionFilter === r ? 'bg-taxera-blue text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                      data-testid={`tracker-region-${r.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {r} ({count})
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div className="relative flex-1 min-w-[180px] max-w-xs ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search countries..."
                  className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal focus:ring-1 focus:ring-taxera-teal/20"
                  data-testid="tracker-search"
                />
              </div>

              {hasFilters && (
                <button
                  onClick={() => { setRegionFilter(null); setStatusFilter(null); setSearch(''); }}
                  className="text-xs font-semibold text-red-500 hover:underline"
                  data-testid="tracker-clear-filters"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Country grid */}
            {loading ? (
              <div className="text-center py-12 text-slate-400">Loading tracker data...</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filtered.map((country, i) => (
                  <motion.div
                    key={country.country_code}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                  >
                    <Link
                      to={`/tracker/${categoryId}/${country.country_code.toLowerCase()}`}
                      className="group block bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg hover:border-taxera-teal/30 transition-all duration-200"
                      data-testid={`tracker-country-${country.country_code.toLowerCase()}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2.5">
                          <img
                            src={`https://flagcdn.com/32x24/${country.country_code.toLowerCase()}.png`}
                            alt={country.country_name}
                            className="w-8 h-6 rounded-sm object-cover border border-slate-200"
                          />
                          <h3 className="font-heading text-sm font-bold text-taxera-charcoal group-hover:text-taxera-blue transition-colors">
                            {country.country_name}
                          </h3>
                        </div>
                        <span
                          className="text-2xs font-bold px-2 py-0.5 rounded-full text-white shrink-0"
                          style={{ backgroundColor: country.status_color || STATUS_COLORS[country.status] }}
                        >
                          {country.status_label}
                        </span>
                      </div>

                      <p className="text-xs text-slate-500 line-clamp-2 mb-3 leading-relaxed">
                        {country.layer1?.mandate_name}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {country.changes_this_quarter > 0 && (
                            <span className="text-2xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-0.5">
                              <Activity className="w-3 h-3" /> {country.changes_this_quarter} update{country.changes_this_quarter > 1 ? 's' : ''}
                            </span>
                          )}
                          <span className="text-2xs text-slate-400">{country.last_updated}</span>
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-taxera-teal group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}

            {filtered.length === 0 && !loading && (
              <div className="text-center py-12 text-slate-400">No countries match your filters.</div>
            )}
          </div>
        </section>
      </div>
    </PageLayout>
  );
}
